import React, { useEffect } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { Form } from '@/shared/components';
import { usePlanVehicleRuleBatchSelections } from '../hooks/usePlanVehicleRuleBatchSelections';
import { usePlanVehicleRuleOptionState } from '../hooks/usePlanVehicleRuleOptionState';
import { planVehicleRuleBatchSchema } from '../schemas/planVehicleRuleBatch.schema';
import type {
    PlanVehicleRuleBatchFormInput,
    PlanVehicleRuleBatchFormOutput,
} from '../schemas/planVehicleRuleBatch.schema';
import type { PlanVehicleRuleBatchPayload } from '../types/planVehicleRule.types';
import { expandVehicleRuleSelections } from '../utils/expandVehicleRuleSelections';
import { toRequires4x4 } from '../utils/fourByFourChoice';
import { batchCountMessage } from '../utils/vehicleRuleBatchCopy';
import { PlanVehicleRuleBatchSelectionFields } from './PlanVehicleRuleBatchSelectionFields';
import { PlanVehicleRuleEngineFields } from './PlanVehicleRuleEngineFields';
import { PlanVehicleRuleAgeFields } from './PlanVehicleRuleAgeFields';
import { PlanVehicleRuleFormActions } from './PlanVehicleRuleFormActions';
import { PlanVehicleRuleHints } from './PlanVehicleRuleHints';

interface Props {
    onSubmit: (payload: PlanVehicleRuleBatchPayload) => void;
    isLoading: boolean;
    error?: string | null;
    onCancel: () => void;
    /** Lets the panel guard row switching so unsaved edits are not silently discarded. */
    onDirtyChange?: (isDirty: boolean) => void;
}

const BATCH_DEFAULTS: PlanVehicleRuleBatchFormInput = {
    make_ids: [],
    transmission_ids: [],
    fuel_type_ids: [],
    model_id: '',
    derivative_id: '',
    engine_min: '',
    engine_max: '',
    age_min: '',
    age_max: '',
    requires_4x4: '',
};

export const PlanVehicleRuleBatchForm: React.FC<Props> = ({ onSubmit, isLoading, error, onCancel, onDirtyChange }) => {
    const {
        handleSubmit,
        control,
        reset,
        setValue,
        clearErrors,
        formState: { isSubmitting, isDirty },
    } = useForm<PlanVehicleRuleBatchFormInput, unknown, PlanVehicleRuleBatchFormOutput>({
        resolver: zodResolver(planVehicleRuleBatchSchema),
        defaultValues: BATCH_DEFAULTS,
    });

    const selections = usePlanVehicleRuleBatchSelections({ control, setValue, clearErrors });
    const {
        fuelTypesQuery,
        transmissionsQuery,
        optionsError,
        cascadeOptions,
        modelValue,
        derivativeOptions,
        derivativeSelected,
        derivativeValue,
        showNoMatchHint,
    } = usePlanVehicleRuleOptionState({
        control,
        filters: selections.filters,
        availableOptions: selections.availableOptions,
        matchingDerivatives: selections.matchingDerivatives,
        isLoadingOptions: selections.isLoadingOptions,
        cascadeOptionsError: selections.optionsError,
        hasMakeAndModel: selections.hasMakeAndModel,
    });
    const makeOptions = selections.availableOptions.makes ?? [];

    useEffect(() => {
        onDirtyChange?.(isDirty);
    }, [isDirty, onDirtyChange]);

    const busy = isSubmitting || isLoading;

    const handleFormSubmit = (data: PlanVehicleRuleBatchFormOutput) => {
        if (selections.overCap) return;
        onSubmit({
            rules: expandVehicleRuleSelections({
                makeIds: data.make_ids,
                transmissionIds: data.transmission_ids,
                fuelTypeIds: data.fuel_type_ids,
                modelId: data.model_id || null,
                derivativeId: data.derivative_id || null,
                engineMin: data.engine_min ?? null,
                engineMax: data.engine_max ?? null,
                ageMin: data.age_min ?? null,
                ageMax: data.age_max ?? null,
                requiresFourByFour: toRequires4x4(data.requires_4x4),
            }),
        });
    };

    const handleClear = () => {
        reset(BATCH_DEFAULTS);
        selections.clearCascade();
    };

    return (
        <Form className="space-y-4" onSubmit={handleSubmit(handleFormSubmit)}>
            <PlanVehicleRuleBatchSelectionFields
                control={control}
                makeOptions={makeOptions}
                transmissionOptions={transmissionsQuery.data ?? []}
                fuelTypeOptions={fuelTypesQuery.data ?? []}
                modelOptions={cascadeOptions.models}
                derivativeOptions={derivativeOptions}
                modelValue={modelValue}
                derivativeValue={derivativeValue}
                onMakesChange={selections.handleMakesChange}
                onModelChange={selections.handleModelChange}
                hasSingleMake={selections.hasSingleMake}
                hasMakeAndModel={selections.hasMakeAndModel}
                derivativeSelected={derivativeSelected}
                isLoadingMakes={selections.isLoadingOptions && makeOptions.length === 0}
                isLoadingModels={selections.isLoadingOptions && !selections.filters.model_id}
                isLoadingTransmissions={transmissionsQuery.isLoading}
                isLoadingFuelTypes={fuelTypesQuery.isLoading}
                busy={busy}
            />

            <PlanVehicleRuleEngineFields control={control} derivativeSelected={derivativeSelected} busy={busy} />

            <PlanVehicleRuleAgeFields control={control} derivativeSelected={derivativeSelected} busy={busy} />

            <PlanVehicleRuleHints isRuleEmpty={selections.isRuleEmpty} showNoMatchHint={showNoMatchHint} />
            <p
                className={selections.overCap ? 'text-xs text-mp-error' : 'text-xs text-mp-muted'}
                role="status"
                data-testid="pvr-batch-count"
            >
                {batchCountMessage(selections.count)}
            </p>

            <PlanVehicleRuleFormActions
                mode="create"
                optionsError={optionsError}
                error={error}
                isLoading={isLoading}
                busy={busy}
                submitDisabled={selections.overCap}
                onSecondaryAction={handleClear}
                onCancel={onCancel}
            />
        </Form>
    );
};
